// Looping, synth-generated background music — no audio files. Shares the
// AudioContext/tone plumbing in audioCore.js with sound.js's one-shot SFX.
// Unlike SFX (default unmuted), music defaults to OFF: starting audio
// requires a user gesture anyway, and unsolicited looping music is more
// intrusive than a one-shot stab.
//
// Each TRACKS entry is a ~90-second song, not a short repeating riff: a
// melody voice built from a handful of distinct phrases (A/B/C) arranged in
// a recognizable song form (e.g. A B A C A B A C), plus a simple bass voice
// outlining the chord progression underneath so it reads as an actual piece
// of music rather than a single beeping line. The three songs auto-advance
// into each other (song 1 -> 2 -> 3 -> back to 1) to form one continuous,
// looping medley; the HUD's "skip" button can still jump ahead manually.

import { getAudioContext, tone } from "./audioCore.js";

const MUTE_KEY = "law:musicMuted";
const TRACK_KEY = "law:musicTrack";

// --- Composition helpers ------------------------------------------------
// Songs are authored as space-separated note-name tokens (e.g. "C5", rest
// "R") rather than raw {freq, duration} objects, so the melodic/harmonic
// intent stays readable. "*N" suffixes a token with a duration multiplier
// in eighth-note units (e.g. "A5*2" = a quarter note at this song's tempo).

const NOTE_FREQS = {
  C2: 65.41, "C#2": 69.3, D2: 73.42, "D#2": 77.78, E2: 82.41, F2: 87.31, "F#2": 92.5, G2: 98.0, "G#2": 103.83, A2: 110.0, "A#2": 116.54, B2: 123.47,
  C3: 130.81, "C#3": 138.59, D3: 146.83, "D#3": 155.56, E3: 164.81, F3: 174.61, "F#3": 185.0, G3: 196.0, "G#3": 207.65, A3: 220.0, "A#3": 233.08, B3: 246.94,
  C4: 261.63, "C#4": 277.18, D4: 293.66, "D#4": 311.13, E4: 329.63, F4: 349.23, "F#4": 369.99, G4: 392.0, "G#4": 415.3, A4: 440.0, "A#4": 466.16, B4: 493.88,
  C5: 523.25, "C#5": 554.37, D5: 587.33, "D#5": 622.25, E5: 659.25, F5: 698.46, "F#5": 739.99, G5: 783.99, "G#5": 830.61, A5: 880.0, "A#5": 932.33, B5: 987.77,
  C6: 1046.5, "C#6": 1108.73, D6: 1174.66, "D#6": 1244.51, E6: 1318.51, F6: 1396.91, "F#6": 1479.98, G6: 1567.98, "G#6": 1661.22, A6: 1760.0, "A#6": 1864.66, B6: 1975.53,
};

function phrase(str, secPerUnit) {
  return str
    .trim()
    .split(/\s+/)
    .map((token) => {
      const [name, mult] = token.split("*");
      const units = mult ? Number(mult) : 1;
      return {
        freq: name === "R" ? 0 : NOTE_FREQS[name],
        duration: Number((units * secPerUnit).toFixed(4)),
      };
    });
}

function totalDuration(notes) {
  return notes.reduce((sum, n) => sum + n.duration, 0);
}

// Builds one song: sequences the named melody phrases per `form`, then
// repeats the (much shorter) bass phrase enough times to cover the same
// total duration, so the two voices realign exactly at the song's end.
function buildTrack(id, name, secPerUnit, bassPhraseStr, melodyPhraseStrs, form) {
  const bassUnit = phrase(bassPhraseStr, secPerUnit);
  const bassUnitDuration = totalDuration(bassUnit);
  const phraseMap = Object.fromEntries(
    Object.entries(melodyPhraseStrs).map(([letter, str]) => [letter, phrase(str, secPerUnit)])
  );
  const melody = form.flatMap((letter) => phraseMap[letter]);
  const melodyDuration = totalDuration(melody);

  const repeats = Math.round(melodyDuration / bassUnitDuration);
  const bass = [];
  for (let i = 0; i < repeats; i += 1) bass.push(...bassUnit);

  return { id, name, melody, bass };
}

// --- The 3-song medley ---------------------------------------------------

export const TRACKS = [
  buildTrack(
    "neon-drift",
    "Neon Drift",
    0.3, // eighth note = 0.3s (~100 BPM) — wistful A-minor synthwave
    // Bass: Am - F - C - G, root/fifth pulse
    "A2 A2 E3 A2 A2 A2 E3 A2 F2 F2 C3 F2 F2 F2 C3 F2 C3 C3 G3 C3 C3 C3 G3 C3 G2 G2 D3 G2 G2 G2 D3 G2",
    {
      A: "A4 C5 E5 A5*2 E5 C5 A4 F4 A4 C5 F5*2 C5 A4 F4 C5 E5 G5 C6*2 G5 E5 C5 G4 B4 D5 G5*2 F5 D5 B4",
      B: "E5 D5 C5 B4 A4 C5 E5 A5 C5 A4 F4 A4 C5 F5 C5 A4 G5 E5 C5 E5 G5 C6 G5 E5 D5 B4 G4 B4 D5 G5 D5 B4",
      C: "A5*2 R E5*2 R C5*2 F5*2 R C5*2 R A4*2 C6*2 R G5*2 R E5*2 G5*2 F5 D5*2 B4 G4*2",
    },
    ["A", "B", "A", "C", "A", "B", "A", "C", "A"] // ~86s
  ),
  buildTrack(
    "pixel-sunrise",
    "Pixel Sunrise",
    0.36, // eighth note = 0.36s (~83 BPM) — warm, uplifting C-major
    // Bass: C - G - Am - F, root/fifth pulse
    "C3 C3 G3 C3 C3 C3 G3 C3 G2 G2 D3 G2 G2 G2 D3 G2 A2 A2 E3 A2 A2 A2 E3 A2 F2 F2 C3 F2 F2 F2 C3 F2",
    {
      A: "C4 E4 G4 C5*2 G4 E4 C4 G4 B4 D5 G5*2 D5 B4 G4 A4 C5 E5 A5*2 E5 C5 A4 F4 A4 C5 F5*2 C5 A4 F4",
      B: "E5 D5 C5 D5 E5 G5 E5 C5 G5 F5 E5 F5 G5 A5 G5 E5 A5 G5 F5 G5 A5 C6 A5 F5 F5 E5 D5 E5 F5 A5 F5 D5",
      C: "C5*2 R G4*2 R E4*2 G5*2 R D5*2 R B4*2 A5*2 R E5*2 R C5*2 F5*2 E5 D5*2 C5 A4*2",
    },
    ["A", "B", "A", "C", "A", "B", "A", "C"] // ~92s
  ),
  buildTrack(
    "arcade-pulse",
    "Arcade Pulse",
    0.2, // eighth note = 0.2s (~150 BPM) — driving E-minor chiptune run
    // Bass: Em - C - G - D, root/fifth pulse
    "E2 E2 B2 E2 E2 E2 B2 E2 C3 C3 G3 C3 C3 C3 G3 C3 G2 G2 D3 G2 G2 G2 D3 G2 D3 D3 A3 D3 D3 D3 A3 D3",
    {
      A: "E4 G4 B4 E5 B4 G4 E4 G4 C4 E4 G4 C5 G4 E4 C4 E4 G4 B4 D5 G5 D5 B4 G4 B4 D4 F#4 A4 D5 A4 F#4 D4 F#4",
      B: "E5 G5 B5 E6 B5 G5 E5 G5 C5 E5 G5 C6 G5 E5 C5 E5 G5 B5 D6 G6 D6 B5 G5 B5 D5 F#5 A5 D6 A5 F#5 D5 F#5",
      C: "E5*2 B4 G4 E4*2 R*2 C5*2 G4 E4 C4*2 R*2 G5*2 D5 B4 G4*2 R*2 D5*2 A4 F#4 D4*2 R*2",
    },
    ["A", "B", "A", "C", "A", "B", "A", "C", "A", "B", "A", "C", "A", "B"] // ~90s
  ),
];

// --- Mute / track persistence --------------------------------------------

function readStoredMuted() {
  if (typeof localStorage === "undefined") return true;
  try {
    const stored = localStorage.getItem(MUTE_KEY);
    return stored === null ? true : stored === "1";
  } catch {
    return true;
  }
}

function readStoredTrackId() {
  if (typeof localStorage === "undefined") return TRACKS[0].id;
  try {
    const stored = localStorage.getItem(TRACK_KEY);
    return TRACKS.some((t) => t.id === stored) ? stored : TRACKS[0].id;
  } catch {
    return TRACKS[0].id;
  }
}

let muted = readStoredMuted();
let currentTrackId = readStoredTrackId();

export function isMusicMuted() {
  return muted;
}

export function setMusicMuted(value) {
  muted = Boolean(value);
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
    } catch {
      // Private browsing / storage quota — muting still works for this tab.
    }
  }
  return muted;
}

export function toggleMusicMuted() {
  return setMusicMuted(!muted);
}

export function getCurrentTrackId() {
  return currentTrackId;
}

function persistTrackId(id) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(TRACK_KEY, id);
  } catch {
    // Private browsing / storage quota — selection still works for this tab.
  }
}

// --- Playback scheduler ---------------------------------------------------
// Standard Web Audio lookahead scheduler, running two independent voices
// (melody + bass) against the current song. Both voices are built to the
// same total duration (see buildTrack), so when the melody voice reaches
// the end of its array the bass voice is finishing at the same instant —
// that's the cue to advance to the next song in TRACKS, wrapping back to
// the first after the last, keeping the timeline continuous (no gap/overlap
// at the seam).

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.1;
const MELODY_GAIN = 0.045;
const BASS_GAIN = 0.03;

let schedulerHandle = null;
let noteIndex = 0;
let nextNoteTime = 0;
let bassIndex = 0;
let bassNextTime = 0;

function resetSchedulePosition() {
  const ctx = getAudioContext();
  noteIndex = 0;
  bassIndex = 0;
  nextNoteTime = ctx ? ctx.currentTime + 0.05 : 0;
  bassNextTime = nextNoteTime;
}

export function setTrack(id) {
  const track = TRACKS.find((t) => t.id === id);
  if (!track) return currentTrackId;
  currentTrackId = id;
  persistTrackId(id);
  if (schedulerHandle) resetSchedulePosition();
  return currentTrackId;
}

export function cycleTrack() {
  const idx = TRACKS.findIndex((t) => t.id === currentTrackId);
  const next = TRACKS[(idx + 1) % TRACKS.length];
  return setTrack(next.id);
}

function scheduleTick() {
  const ctx = getAudioContext();
  if (!ctx) return;
  let track = TRACKS.find((t) => t.id === currentTrackId) || TRACKS[0];

  while (nextNoteTime < ctx.currentTime + SCHEDULE_AHEAD_S) {
    if (noteIndex >= track.melody.length) {
      const idx = TRACKS.findIndex((t) => t.id === track.id);
      const next = TRACKS[(idx + 1) % TRACKS.length];
      currentTrackId = next.id;
      persistTrackId(next.id);
      track = next;
      noteIndex = 0;
      bassIndex = 0;
      bassNextTime = nextNoteTime;
    }
    const note = track.melody[noteIndex];
    if (note.freq > 0) {
      tone(ctx, { freq: note.freq, start: nextNoteTime, duration: note.duration, type: "square", gain: MELODY_GAIN });
    }
    nextNoteTime += note.duration;
    noteIndex += 1;
  }

  while (bassNextTime < ctx.currentTime + SCHEDULE_AHEAD_S) {
    if (bassIndex >= track.bass.length) bassIndex = 0;
    const note = track.bass[bassIndex];
    if (note.freq > 0) {
      tone(ctx, { freq: note.freq, start: bassNextTime, duration: note.duration, type: "triangle", gain: BASS_GAIN });
    }
    bassNextTime += note.duration;
    bassIndex += 1;
  }
}

export function startMusic() {
  if (muted) return;
  if (schedulerHandle) return; // already running
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    resetSchedulePosition();
    schedulerHandle = setInterval(scheduleTick, LOOKAHEAD_MS);
  } catch {
    // Audio is a nice-to-have — never let a scheduling failure break the app.
  }
}

export function stopMusic() {
  if (schedulerHandle) {
    clearInterval(schedulerHandle);
    schedulerHandle = null;
  }
}
